import React, { useContext, useEffect } from "react";
import AgGridLayout from "@wrappers/layouts/AgGridLayout";
import {
    RowNode,
    ValueFormatterParams,
} from "ag-grid-community";
import { titleCase, numericValueFormatter, formatCurrency, isDisabledUI } from "@ts/utils";
import { customFilterOptions, mainContext } from "@ts/constants";
import {
    ChangeInValueCellRenderer,
    FilterTextFormatter,
    StatusBadgeCellRenderer,
    StatusCellRenderer,
    TargetCellRenderer,
    UpdateBidOrBudgetCellRenderer
} from "@cell-renderers/AllCellRenderers";
import { useUpdateModule, useGetModulesData } from "@hooks/AllCustomHooks";
import { useSelector } from "react-redux";
import AddRulesModal from "@modals/rules-modal/AddRulesModal";
import { onAdGroupSelectionChanged } from "@ts/performance-overview";
import { IAdgroups, ICustomCellRendererParams, IMainContext } from "@ts/common-types";

const AdGroups = React.memo(() => {

    const adgroupsData = useSelector(
        (state: IAdgroups) => state.adgroupsData.value
    );

    const { adgroupLoading, adgroupsGridApi, setAdgroupsGridApi } =
        useContext<IMainContext>(mainContext);

    const updateModule = useUpdateModule("adgroups");
    const gridParams = useGetModulesData({ type: "grid-params" });

    useEffect(() => {
        sessionStorage.setItem("trendType", "adgroups");
    }, [adgroupsData]);

    const adGroupsColumnDefs = useGetModulesData({
        type:"columns",
        colDefs:[
        {
            field: "ad_group_name",
            headerName: "Ad Group",
            cellStyle: { whiteSpace: "normal" },
            checkboxSelection: true,
            pinned: "left",
            lockPinned: true,
            cellClass: "lock-pinned",
            minWidth: 350,
            maxWidth: 500,
            filter: "agTextColumnFilter",
            tooltipField: "ad_group_name",
            cellRenderer: TargetCellRenderer,
            headerCheckboxSelection: true,
            valueFormatter: (params: ValueFormatterParams) => {
                return titleCase(params.value || "");
            },
            cellRendererParams: {
                hasPinnedColumnValue: true,
                pinnedColumnValue: "Total",
                    origin: "adgroups",
                    isBadgeRequired: !!gridParams?.isBadgeRequired,
                    isTypeBadgeRequired: !!gridParams?.isTypeBadgeRequired,
                },
            },
            {
                field: "budget",
                headerName: "Budget",
                filter: "agNumberColumnFilter",
                minWidth: gridParams["budget"].minWidth,
                maxWidth: gridParams["budget"].maxWidth,
                cellClass: "lock-pinned",
                pinned: "left",
                valueFormatter: (param: ValueFormatterParams) =>
                    !param.node.rowPinned
                        ? formatCurrency(param.value, param.data.country)
                        : "",
                cellRenderer: UpdateBidOrBudgetCellRenderer,
                cellRendererParams: {
                    hasPinnedColumnValue: false,
                },
            },
        {
            field: "state",
            headerName: "Status",
            maxWidth: 100,
            minWidth: 180,
            filter: true,
            pinned: "left",
            lockPinned: true,
            cellClass: "lock-pinned",
            cellStyle: { whiteSpace: "normal" },
            cellRenderer: (params: ICustomCellRendererParams) =>
                StatusCellRenderer(params, "ad-group"),
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "campaign_name",
            headerName: "Campaign",
            filter: "agTextColumnFilter",
            tooltipField: "campaign_name",
            cellRenderer: StatusBadgeCellRenderer,
            minWidth: 160,
            cellRendererParams: {
                hasPinnedColumnValue: false,
                isBadgeRequired: !!gridParams?.isBadgeRequired,
                isTypeBadgeRequired: !!gridParams?.isTypeBadgeRequired,
            },
        },
        {
            field: "portfolio_name",
            headerName: "Portfolio",
            filter: "agTextColumnFilter",
            cellStyle: { whiteSpace: "normal" },
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "program_type",
            headerName: "Ad Type",
            filter: "agSetColumnFilter",
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
            cellStyle: { whiteSpace: "normal" },
            cellClass: "text-center",
            valueFormatter: (params: ValueFormatterParams) =>
                params.value ? params.value.toUpperCase() : params.value,
            hide: true,
            filterParams: {
                valueFormatter: FilterTextFormatter
            },
        },
        {
            headerName: "Spends",
            cellStyle: { whiteSpace: "normal" },
            field: "spend",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            sort: "desc",
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "Orders",
            cellStyle: { whiteSpace: "normal" },
            field: "orders",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
            },
        },
        {
            headerName: "Sales",
            cellStyle: { whiteSpace: "normal" },
            field: "sale",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },

        {
            headerName: "Impressions",
            cellStyle: { whiteSpace: "normal" },
            field: "impression",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
            },
        },
        {
            headerName: "Clicks",
            cellStyle: { whiteSpace: "normal" },
            field: "click",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
                decimalPlace: 0,
            },
            hide: true,
        },
        {
            headerName: "CTR",
            cellStyle: { whiteSpace: "normal" },
            field: "ctr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: true,
                showChangePercent: true,
                decimalPlace: 1,
            },
        },
        {
            headerName: "CPC",
            cellStyle: { whiteSpace: "normal" },
            field: "cpc",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
            hide: true,
        },
        {
            headerName: "CR",
            cellStyle: { whiteSpace: "normal" },
            field: "cr",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: true,
                showChangePercent: true,
            },
            hide: true,
        },
        {
            headerName: "CPA",
            cellStyle: { whiteSpace: "normal" },
            field: "cpa",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
            hide: true,
        },
        {
            headerName: "ACoS",
            cellStyle: { whiteSpace: "normal" },
            field: "acos",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            hide: true,
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: true,
                showChangePercent: true,
            },
        },
        {
            headerName: "RoAS",
            cellStyle: { whiteSpace: "normal" },
            field: "roas",
            filter: "agNumberColumnFilter",
            filterParams: {
                filterOptions: customFilterOptions["number"],
            },
            cellRenderer: ChangeInValueCellRenderer,
            valueFormatter: numericValueFormatter,
            cellRendererParams: {
                hasPinnedColumnValue: true,
                hasPercent: false,
                showChangePercent: true,
            },
        },
        {
            headerName: "Top ASIN",
            cellStyle: { whiteSpace: "normal" },
            field: "top_product",
            filter: "agSetColumnFilter",
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            headerName: "% Spend",
            cellStyle: { whiteSpace: "normal" },
            field: "percent_spend",
            hide: true,
        },
        {
            field: "sys_campaign_id",
            filter: "agSetColumnFilter",
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "sys_adgroup_id",
            filter: "agSetColumnFilter",
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "asin_id",
            filter: "agSetColumnFilter",
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
        {
            field: "portfolio_id",
            filter: "agSetColumnFilter",
            hide: true,
            cellRendererParams: {
                hasPinnedColumnValue: false,
            },
        },
    ]});

    return (
        <>
            <div className="wrapper" style={{ flex: "1 1 0" }}>
                <div
                    id="adgroups-grid"
                    className="ag-theme-alpine"
                    style={{ height: "100%", width: "100%" }}
                >
                    <AgGridLayout
                        rowData={adgroupsData}
                        columnDefs={adGroupsColumnDefs}
                        setGridApi={setAdgroupsGridApi}
                        onSelectionChanged={onAdGroupSelectionChanged}
                        updateModule={updateModule}
                        loading={adgroupLoading}
                        rowHeight={gridParams?.rowHeight}
                        isTotalRowsReq={true}
                        isRowSelectable={(rowNode: RowNode) => !isDisabledUI([rowNode.data.adgroup_status, rowNode.data.state])
                            }
                    ></AgGridLayout>
                </div>
                <AddRulesModal api={adgroupsGridApi} />
            </div>
        </>
    );
});

export default AdGroups;
